import {
  BoxGeometry,
  DodecahedronGeometry,
  EdgesGeometry,
  Euler,
  GridHelper,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three';
import { Prop } from '../entities/Prop.js';
import { GROUP, MASK } from '../physics/groups.js';
import { createNoiseTexture } from '../utils/textures.js';
import { createRandom } from '../utils/random.js';
import { disposeObject3D } from '../utils/dispose.js';

/**
 * Builds a level from plain data. All solid objects are Prop entities so they
 * get a matching Bullet body and can be reset/disposed consistently.
 */
export class Level {
  constructor({ world, data, renderer, colors, config = null }) {
    this.world = world;
    this.data = data;
    this.renderer = renderer;
    this.colors = colors;
    this.config = config;

    this.name = data.name ?? 'Untitled level';
    this.spawn = new Vector3(...(data.spawn?.position ?? [0, 2, 8]));
    this.spawnYaw = data.spawn?.yaw ?? 0;
    this.entities = [];

    this.root = new Group();
    world.scene.add(this.root);

    // A level can either use the generic desert-demo data below or provide a
    // build hook for a hand-built world (see levels/khartoum.js).
    if (typeof data.build === 'function') {
      data.build(this);
    } else {
      this.#buildGround();
      this.#buildStatics();
      this.#buildCrates();
      this.#buildSpheres();
      this.#buildPebbles();
    }
  }

  register(prop) {
    this.world.add(prop);
    this.entities.push(prop);
    return prop;
  }

  update(dt, player) {
    this.onUpdate?.(dt, player);
  }

  #quaternionFromRotation(rotation = null) {
    if (!rotation) return new Quaternion();
    return new Quaternion().setFromEuler(new Euler(rotation[0], rotation[1], rotation[2]));
  }

  #buildGround() {
    const size = this.data.ground?.size ?? 160;

    const texture = createNoiseTexture({
      size: 256,
      base: this.colors.sand,
      speckle: this.colors.sandSpeckle,
      count: 4200,
      repeat: 26,
      seed: 5,
      anisotropy: Math.min(this.renderer.capabilities.getMaxAnisotropy(), 8),
    });

    const material = new MeshStandardMaterial({
      map: texture,
      roughness: 1,
      metalness: 0,
    });

    const mesh = new Mesh(new BoxGeometry(size, 1, size), material);
    mesh.receiveShadow = true;

    const ground = new Prop({
      physics: this.world.physics,
      mesh,
      shape: { type: 'box', size: [size, 1, size] },
      mass: 0,
      position: [0, -0.5, 0],
      friction: 1,
      restitution: 0,
      group: GROUP.LEVEL,
      mask: MASK.LEVEL,
      userData: { surface: 'sand' },
    });

    this.register(ground);

    const grid = new GridHelper(
      Math.min(size, 130),
      Math.min(size, 130),
      this.colors.grid,
      this.colors.grid,
    );
    grid.position.y = 0.012;
    grid.material.transparent = true;
    grid.material.opacity = 0.16;
    grid.material.depthWrite = false;
    this.root.add(grid);
  }

  #buildStatics() {
    for (const entry of this.data.statics ?? []) {
      const {
        position = [0, 0, 0],
        size = [1, 1, 1],
        rotation = null,
        color = this.colors.stone,
        friction = 1,
        restitution = 0,
      } = entry;

      const mesh = this.#createBoxMesh(size, color, {
        edgeOpacity: 0.45,
        roughness: 0.92,
      });

      const quaternion = this.#quaternionFromRotation(rotation);

      const prop = new Prop({
        physics: this.world.physics,
        mesh,
        shape: { type: 'box', size },
        mass: 0,
        position,
        quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
        friction,
        restitution,
        group: GROUP.LEVEL,
        mask: MASK.LEVEL,
        userData: { surface: 'stone' },
      });

      this.register(prop);
    }
  }

  #buildCrates() {
    for (const entry of this.data.crates ?? []) {
      const {
        position,
        size = [1, 1, 1],
        rotation = null,
        mass = 4,
        color = this.colors.crate,
      } = entry;

      const mesh = this.#createBoxMesh(size, color, {
        edgeOpacity: 0.65,
        roughness: 0.82,
      });

      const quaternion = this.#quaternionFromRotation(rotation);

      const prop = new Prop({
        physics: this.world.physics,
        mesh,
        shape: { type: 'box', size },
        mass,
        position,
        quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
        friction: 0.8,
        restitution: 0.02,
        linearDamping: 0.05,
        angularDamping: 0.15,
        group: GROUP.PROP,
        mask: MASK.PROP,
        userData: { surface: 'wood' },
      });

      this.register(prop);
    }
  }

  #buildSpheres() {
    for (const entry of this.data.spheres ?? []) {
      const {
        position,
        radius = 0.5,
        mass = 2,
        color = this.colors.ball,
      } = entry;

      const mesh = new Mesh(
        new SphereGeometry(radius, 26, 18),
        new MeshStandardMaterial({
          color,
          roughness: 0.35,
          metalness: 0.35,
        }),
      );
      mesh.castShadow = true;
      mesh.receiveShadow = true;

      const prop = new Prop({
        physics: this.world.physics,
        mesh,
        shape: { type: 'sphere', radius },
        mass,
        position,
        friction: 0.6,
        restitution: 0.35,
        linearDamping: 0.05,
        angularDamping: 0.08,
        group: GROUP.PROP,
        mask: MASK.PROP,
        userData: { surface: 'metal' },
      });

      this.register(prop);
    }
  }

  #createBoxMesh(size, color, { edgeOpacity = 0.45, roughness = 0.85 } = {}) {
    const geometry = new BoxGeometry(size[0], size[1], size[2]);
    const material = new MeshStandardMaterial({
      color,
      roughness,
      metalness: 0.03,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 1,
    });

    const box = new Mesh(geometry, material);
    box.castShadow = true;
    box.receiveShadow = true;

    const group = new Group();
    group.add(box);

    const edges = new LineSegments(
      new EdgesGeometry(geometry),
      new LineBasicMaterial({
        color: this.colors.crateEdge,
        transparent: true,
        opacity: edgeOpacity,
      }),
    );
    group.add(edges);

    return group;
  }

  #buildPebbles() {
    const random = createRandom(41);
    const geometry = new DodecahedronGeometry(1, 0);
    const materials = [
      new MeshStandardMaterial({ color: 0xa99070, roughness: 1, flatShading: true }),
      new MeshStandardMaterial({ color: 0x9c8667, roughness: 1, flatShading: true }),
      new MeshStandardMaterial({ color: 0xb49b7b, roughness: 1, flatShading: true }),
    ];

    for (let i = 0; i < 30; i += 1) {
      const radius = random.range(18, 52);
      const angle = random() * Math.PI * 2;
      const scaleX = random.range(0.18, 0.75);
      const scaleY = random.range(0.12, 0.5);
      const scaleZ = random.range(0.18, 0.7);

      const pebble = new Mesh(geometry, random.pick(materials));
      pebble.position.set(
        Math.cos(angle) * radius,
        scaleY * 0.45,
        Math.sin(angle) * radius,
      );
      pebble.scale.set(scaleX, scaleY, scaleZ);
      pebble.rotation.set(random() * Math.PI, random() * Math.PI, random() * Math.PI);
      pebble.castShadow = true;
      pebble.receiveShadow = true;
      this.root.add(pebble);
    }
  }

  dispose() {
    for (const entity of this.entities) {
      this.world.remove(entity);
      disposeObject3D(entity.mesh);
    }
    this.entities.length = 0;

    disposeObject3D(this.root);
    this.root.clear();
  }
}
